import { Handle, Position } from 'reactflow';
import { TransactionsCommonMappingModalContentBoardNodeProps } from './props';
import styles from './styles.module.scss';
import { nodeLabelHandler } from './helper';
import { useMemo } from 'react';
import { NodeType } from '@starsoft/common/hooks/transactions/useNodesFromTransactions/props';
import { Image } from '@starsoft/common/components/Image';
import { SettingsService, WalletService } from '@starsoft/common/services';
import { getMoneyOrAmount } from '@starsoft/common/utils';

/**
 * TransactionsCommonMappingModalContentBoardNode Component
 * This component is responsible for rendering a node in the transaction mapping board.
 * It displays the node's label and, if the node type is Coin, it also displays the coin's image and amount.
 *
 * @param {TransactionsCommonMappingModalContentBoardNodeProps} props - The properties for the component.
 * @param {Object} props.transfer - The transfer object containing transaction details.
 * @param {NodeType} props.type - The type of the node, which determines its rendering logic.
 *
 * @returns {JSX.Element} The rendered node component with handles for connecting edges.
 *
 * @example
 * // Usage example:
 * <TransactionsCommonMappingModalContentBoardNode
 *   transfer={{ amount: new Decimal(100), coin: { id: 'btc', image: 'btc.svg', name: 'Bitcoin' } }}
 *   type={NodeType.Coin}
 * />
 */
export function TransactionsCommonMappingModalContentBoardNode({
  transfer,
  type,
}: TransactionsCommonMappingModalContentBoardNodeProps) {
  // Retrieve the base URL for the S3 bucket from the settings service
  const bucketUrl = SettingsService.env.S3_BUCKET_BASE_URL;

  // Memoize the label for the node using the nodeLabelHandler function
  const label = useMemo(
    () => nodeLabelHandler(transfer, type),
    [transfer, type],
  );

  return (
    <>
      {/* Render a handle for connecting edges to the left side of the node */}
      <Handle
        type="target"
        position={Position.Left}
        id={String(label ?? transfer?.coin?.id)}
      />
      <div
        className={styles.container}
        id={String(label ?? transfer?.coin?.id)}
      >
        {/* Display the node's label */}
        {label}
        {type == NodeType.Coin && (
          <div
            className={styles.container__coin}
            id={String(label ?? transfer?.coin?.id)}
          >
            {/* Render the coin's image */}
            <Image
              id={String(label ?? transfer?.coin?.id)}
              width={32}
              height={32}
              className={styles.container__coin__image}
              src={`${bucketUrl}/coins/rounded/${transfer?.coin?.image ?? 'brl.svg'}`}
              alt={`coin-symbol-${transfer?.coin?.name}`}
            />
            <div
              className={styles.container__coin__amount}
              id={String(label ?? transfer?.coin?.id)}
            >
              {/* Display the masked currency amount */}
              {WalletService.maskCurrency({
                amount: getMoneyOrAmount(transfer?.amount),
                coin: transfer?.coin,
                hideSymbol: true,
              })}
            </div>
          </div>
        )}
      </div>
      {/* Render a handle for connecting edges to the right side of the node */}
      <Handle
        type="target"
        position={Position.Right}
        id={String(label ?? transfer?.coin?.id)}
      />
    </>
  );
}
